import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  handleRegisterCustomer,
  handleRegisterSupplier,
  handleVerifyCode,
  handleResendCode,
} from '../controllers/authController';
import { secureStorage } from '../utils/security';
import { CITIES } from '../models/cityModel';
import { PART_CATEGORIES } from '../models/partModel';
import Turnstile, { captchaEnabled } from '../components/Turnstile';
import TaxDocumentInput from '../components/TaxDocumentInput';

const inp = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition';
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';
const CATS = ['Tüm Kategoriler', ...PART_CATEGORIES];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [tab,     setTab]     = useState('customer');
  const [step,    setStep]    = useState('form');
  const [userId,  setUserId]  = useState(null);
  const [code, setCode] = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [resent,  setResent]  = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const [cf, setCf] = useState({ full_name:'', phone:'', email:'', password:'', confirm_password:'', terms:false, marketing:false });
  const [sf, setSf] = useState({ firm_name:'', tax_no:'', city:'', phone:'', email:'', password:'', confirm_password:'', category:'', terms:false, marketing:false });
  const [taxDoc, setTaxDoc] = useState(null);   // vergi levhası (File)

  const setCfField = (k, v) => setCf(p => ({ ...p, [k]: v }));
  const setSfField = (k, v) => setSf(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setError('');
    const accepted = tab === 'customer' ? cf.terms : sf.terms;
    if (!accepted) return setError('Devam etmek için sözleşme ve politikaları kabul etmelisiniz.');
    if (captchaEnabled && !captchaToken) return setError('Lütfen güvenlik doğrulamasını tamamlayın.');
    setLoading(true);
    const result = tab === 'customer'
      ? await handleRegisterCustomer(cf, captchaToken)
      : await handleRegisterSupplier(sf, captchaToken, taxDoc);
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    setUserId(result.user_id);
    setStep('verify');
  };

  const verifyCode = async () => {
    setError(''); setLoading(true);
    const result = await handleVerifyCode(userId, code);
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    secureStorage.setToken(result.token);
    login(result.user);
    const role = result.user?.role;
    if (role === 'admin')         navigate('/dashboard/admin');
    else if (role === 'supplier') navigate('/dashboard/supplier');
    else                          navigate('/dashboard/customer');
  };

  const resendCode = async () => {
    setResent(false);
    const result = await handleResendCode(userId);
    if (!result.error) setResent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-2xl w-full max-w-lg p-10 shadow-2xl">

        <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-blue-700 flex items-center gap-1 mb-6 transition">
          ← Ana Sayfa
        </button>

        <div className="text-2xl font-bold text-gray-900 mb-1">Oto<span className="text-blue-700">ParçamıBul</span></div>
        <h2 className="text-xl font-bold text-gray-800 mb-1">Ücretsiz Hesap Oluştur</h2>
        <p className="text-gray-400 text-sm mb-6">Türkiye'nin oto parça platformuna katılın.</p>

        {step === 'form' && (
          <>
            {/* Account-type selector */}
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Hesap Türü</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                { key: 'customer', icon: '🔍', title: 'Parça Arıyorum', desc: 'Müşteri — parça talebi oluştururum',
                  sel: 'border-blue-600 bg-blue-50 ring-2 ring-blue-100', titleSel: 'text-blue-700', check: 'text-blue-600' },
                { key: 'supplier', icon: '🏢', title: 'Tedarikçiyim',   desc: 'Satıcı — taleplere teklif veririm',
                  sel: 'border-purple-600 bg-purple-50 ring-2 ring-purple-100', titleSel: 'text-purple-700', check: 'text-purple-600' },
              ].map(t => (
                <button key={t.key} type="button" onClick={() => { setTab(t.key); setError(''); }}
                  className={`text-left p-3.5 rounded-xl border-2 transition
                    ${tab === t.key ? t.sel : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{t.icon}</span>
                    <span className={`text-sm font-bold ${tab === t.key ? t.titleSel : 'text-gray-700'}`}>{t.title}</span>
                    {tab === t.key && <span className={`ml-auto text-sm font-bold ${t.check}`}>✓</span>}
                  </div>
                  <p className="text-xs text-gray-400 leading-snug">{t.desc}</p>
                </button>
              ))}
            </div>

            {error && <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm mb-5">⚠️ {error}</div>}

            {/* Customer */}
            {tab === 'customer' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Ad Soyad *</label>
                    <input type="text" className={inp} placeholder="Adınız Soyadınız" value={cf.full_name} maxLength={100} onChange={e=>setCfField('full_name',e.target.value)}/></div>
                  <div><label className={lbl}>Telefon *</label>
                    <input type="tel" className={inp} placeholder="05xx xxx xx xx" value={cf.phone} maxLength={15} onChange={e=>setCfField('phone',e.target.value)}/></div>
                </div>
                <div><label className={lbl}>E-posta *</label>
                  <input type="email" className={inp} placeholder="email@adresiniz.com" value={cf.email} maxLength={254} onChange={e=>setCfField('email',e.target.value)}/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Şifre *</label>
                    <input type="password" className={inp} placeholder="Min. 8 karakter" value={cf.password} maxLength={128} onChange={e=>setCfField('password',e.target.value)}/></div>
                  <div><label className={lbl}>Şifre Tekrar *</label>
                    <input type="password" className={inp} placeholder="Tekrar girin" value={cf.confirm_password} maxLength={128} onChange={e=>setCfField('confirm_password',e.target.value)}/></div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-600">
                  <input type="checkbox" className="mt-0.5 accent-blue-700" checked={cf.terms} onChange={e=>setCfField('terms',e.target.checked)}/>
                  <span className="leading-snug">
                    <a href="/yasal/kullanim-sartlari" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">Kullanım Şartları</a>’nı,{' '}
                    <a href="/yasal/gizlilik-politikasi" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">Gizlilik Politikası</a>’nı ve{' '}
                    <a href="/yasal/kvkk-aydinlatma" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">KVKK Aydınlatma Metni</a>’ni okudum, kabul ediyorum.
                  </span>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-500">
                  <input type="checkbox" className="mt-0.5 accent-blue-700" checked={cf.marketing} onChange={e=>setCfField('marketing',e.target.checked)}/>
                  <span className="leading-snug">Kampanya ve duyurulardan e-posta/SMS ile haberdar olmak istiyorum. <span className="text-gray-400">(İsteğe bağlı)</span></span>
                </div>
              </div>
            )}

            {/* Supplier */}
            {tab === 'supplier' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Firma Adı *</label>
                    <input type="text" className={inp} placeholder="Oto Yedek A.Ş." value={sf.firm_name} maxLength={150} onChange={e=>setSfField('firm_name',e.target.value)}/></div>
                  <div><label className={lbl}>Vergi / T.C. No *</label>
                    <input type="text" inputMode="numeric" className={inp} placeholder="10 veya 11 hane" value={sf.tax_no} maxLength={11} onChange={e=>setSfField('tax_no',e.target.value.replace(/\D/g,''))}/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Şehir *</label>
                    <select className={inp} value={sf.city} onChange={e=>setSfField('city',e.target.value)}>
                      <option value="">Seçin</option>{CITIES.map(c=><option key={c}>{c}</option>)}
                    </select></div>
                  <div><label className={lbl}>Telefon *</label>
                    <input type="tel" className={inp} placeholder="05xx xxx xx xx" value={sf.phone} maxLength={15} onChange={e=>setSfField('phone',e.target.value)}/></div>
                </div>
                <div><label className={lbl}>E-posta *</label>
                  <input type="email" className={inp} placeholder="firma@email.com" value={sf.email} maxLength={254} onChange={e=>setSfField('email',e.target.value)}/></div>
                <div><label className={lbl}>Uzmanlık Kategorisi</label>
                  <select className={inp} value={sf.category} onChange={e=>setSfField('category',e.target.value)}>
                    <option value="">Seçin</option>{CATS.map(c=><option key={c}>{c}</option>)}
                  </select></div>
                <div><label className={lbl}>Vergi Levhası *</label>
                  <TaxDocumentInput file={taxDoc} onChange={setTaxDoc} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Şifre *</label>
                    <input type="password" className={inp} placeholder="Min. 8 karakter" value={sf.password} maxLength={128} onChange={e=>setSfField('password',e.target.value)}/></div>
                  <div><label className={lbl}>Şifre Tekrar *</label>
                    <input type="password" className={inp} placeholder="Tekrar girin" value={sf.confirm_password} maxLength={128} onChange={e=>setSfField('confirm_password',e.target.value)}/></div>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-600">
                  <input type="checkbox" className="mt-0.5 accent-purple-700" checked={sf.terms} onChange={e=>setSfField('terms',e.target.checked)}/>
                  <span className="leading-snug">
                    <a href="/yasal/tedarikci-sozlesmesi" target="_blank" rel="noreferrer" className="text-purple-700 hover:underline font-semibold">Tedarikçi/Satıcı Sözleşmesi</a>’ni,{' '}
                    <a href="/yasal/kullanim-sartlari" target="_blank" rel="noreferrer" className="text-purple-700 hover:underline">Kullanım Şartları</a>’nı,{' '}
                    <a href="/yasal/kurallar" target="_blank" rel="noreferrer" className="text-purple-700 hover:underline">İlan/Teklif Kuralları</a>’nı,{' '}
                    <a href="/yasal/gizlilik-politikasi" target="_blank" rel="noreferrer" className="text-purple-700 hover:underline">Gizlilik Politikası</a>’nı ve{' '}
                    <a href="/yasal/kvkk-aydinlatma" target="_blank" rel="noreferrer" className="text-purple-700 hover:underline">KVKK Aydınlatma Metni</a>’ni okudum, kabul ediyorum.
                  </span>
                </div>
                <div className="flex items-start gap-3 text-sm text-gray-500">
                  <input type="checkbox" className="mt-0.5 accent-purple-700" checked={sf.marketing} onChange={e=>setSfField('marketing',e.target.checked)}/>
                  <span className="leading-snug">Kampanya ve duyurulardan e-posta/SMS ile haberdar olmak istiyorum. <span className="text-gray-400">(İsteğe bağlı)</span></span>
                </div>
              </div>
            )}

            <Turnstile onVerify={setCaptchaToken} />

            <button onClick={submit} disabled={loading || (captchaEnabled && !captchaToken)}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition mt-6">
              {loading ? 'Kayıt yapılıyor...' : 'Kayıt Ol →'}
            </button>
          </>
        )}

        {/* Email verification step */}
        {step === 'verify' && (
          <div>
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">✉️</div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">E-posta Doğrulama</h3>
              <p className="text-gray-400 text-sm">
                {tab === 'customer' ? cf.email : sf.email} adresine gönderilen 6 haneli kodu girin.
              </p>
            </div>
            {error  && <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm mb-4">⚠️ {error}</div>}
            {resent && <div className="bg-green-50 border border-green-100 text-green-700 rounded-xl px-4 py-3 text-sm mb-4">✓ Yeni kod gönderildi.</div>}
            <input type="text" className={`${inp} text-center text-2xl tracking-[0.5em] font-mono mb-4`}
              placeholder="000000" maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
              onKeyDown={e => e.key === 'Enter' && verifyCode()}
            />
            <button onClick={verifyCode} disabled={loading || code.length !== 6}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition mb-3">
              {loading ? 'Doğrulanıyor...' : 'Doğrula & Hesabı Aktifleştir'}
            </button>
            <div className="flex justify-between text-xs text-gray-400">
              <button onClick={() => setStep('form')} className="hover:text-blue-700 transition">← Geri</button>
              <button onClick={resendCode} className="hover:text-blue-700 transition">Kod gelmedi? Tekrar gönder</button>
            </div>
          </div>
        )}

        <p className="text-center text-sm text-gray-400 mt-5">
          Zaten hesabınız var mı?{' '}
          <button onClick={() => navigate('/login')} className="text-blue-700 font-semibold hover:underline">Giriş Yapın</button>
        </p>
      </div>
    </div>
  );
}